import type { HiveWorkflow, WfControls, WfTemplate } from "@/types";
import { buildGraph, nodeDef } from "@/data/mockWorkflowNodes";

const baseControls: WfControls = {
  maxRunsPerHour: 30,
  maxCostPerRun: 12,
  approvalCostThreshold: 8,
  pauseOnRepeatedFailures: true,
  disableExternalInSandbox: true,
  blockProdDeployWithoutAdmin: true,
};

function integrationsOf(hooks: string[], procs: string[], pubs: string[]) {
  const set = new Set<string>();
  [...hooks, ...procs, ...pubs].forEach((k) => {
    const i = nodeDef(k).integration;
    if (i && i !== "hive") set.add(i);
  });
  return [...set];
}

interface Spec {
  id: string;
  name: string;
  description: string;
  status: HiveWorkflow["status"];
  env: HiveWorkflow["env"];
  owner: string;
  version: string;
  risk: HiveWorkflow["risk"];
  triggerHook: string;
  lastRun: string;
  successRate: number;
  avgDuration: string;
  runsThisWeek: number;
  failedRuns: number;
  hooks: string[];
  procs: string[];
  pubs: string[];
  controls?: Partial<WfControls>;
  cost: HiveWorkflow["cost"];
  history: HiveWorkflow["history"];
  /** indices (into the flat hook→proc→pub order) of nodes to mark incomplete */
  incomplete?: number[];
}

const SPECS: Spec[] = [
  {
    id: "wf-jira-ares",
    name: "Jira Ticket to ARES PR",
    description:
      "Turn a refined Jira ticket into a verified pull request with ARES, then update Jira and notify the team.",
    status: "active",
    env: "production",
    owner: "Priya Nair",
    version: "v4.2",
    risk: "medium",
    triggerHook: "Jira Ticket Created",
    lastRun: "8m ago",
    successRate: 0.962,
    avgDuration: "14m 20s",
    runsThisWeek: 48,
    failedRuns: 2,
    hooks: ["hook.jira.created"],
    procs: [
      "proc.refine",
      "proc.create-ws",
      "proc.create-branch",
      "proc.assign-ares",
      "proc.run-ares",
      "proc.verify",
    ],
    pubs: ["pub.github.pr", "pub.jira.update", "pub.slack.post"],
    cost: { model: 2.4, cloud: 1.1, verification: 0.6, byoncSavings: 1.8, estRuntime: "14m" },
    history: [
      { version: "v4.2", at: "2d ago", by: "Priya Nair", note: "Added verification gate before PR" },
      { version: "v4.1", at: "1w ago", by: "Priya Nair", note: "Switched ARES to cost-optimized routing" },
      { version: "v4.0", at: "3w ago", by: "Marcus Webb", note: "Promoted to production" },
    ],
  },
  {
    id: "wf-ci-triage",
    name: "CI Failure Auto-Triage",
    description:
      "Investigate failing CI runs automatically — analyze logs, spin up a debug workspace, ask ARES to investigate and open a fix PR.",
    status: "active",
    env: "production",
    owner: "Marcus Webb",
    version: "v2.7",
    risk: "medium",
    triggerHook: "CI Failed",
    lastRun: "22m ago",
    successRate: 0.884,
    avgDuration: "9m 05s",
    runsThisWeek: 31,
    failedRuns: 4,
    hooks: ["hook.ci.failed"],
    procs: ["proc.analyze-logs", "proc.create-ws", "proc.run-ares", "proc.verify"],
    pubs: ["pub.github.comment", "pub.slack.post", "pub.notify"],
    cost: { model: 1.6, cloud: 0.9, verification: 0.5, byoncSavings: 1.2, estRuntime: "9m" },
    history: [
      { version: "v2.7", at: "4d ago", by: "Marcus Webb", note: "Added flake detection short-circuit" },
      { version: "v2.6", at: "2w ago", by: "Marcus Webb", note: "Tuned log analysis prompt" },
    ],
  },
  {
    id: "wf-api-drift",
    name: "API Drift Watcher",
    description:
      "Watch for OpenAPI spec changes, run an ATHENA readiness check, detect SDK drift and generate fresh agent assets.",
    status: "draft",
    env: "sandbox",
    owner: "Dana Lee",
    version: "v0.9",
    risk: "low",
    triggerHook: "Webhook Received",
    lastRun: "—",
    successRate: 0.0,
    avgDuration: "—",
    runsThisWeek: 0,
    failedRuns: 0,
    hooks: ["hook.webhook"],
    procs: ["proc.athena", "proc.deps", "proc.summarize"],
    pubs: ["pub.jira.update", "pub.slack.post", "pub.artifact"],
    incomplete: [1],
    cost: { model: 0.9, cloud: 0.3, verification: 0.2, byoncSavings: 0.7, estRuntime: "5m" },
    history: [
      { version: "v0.9", at: "1d ago", by: "Dana Lee", note: "Draft — wiring ATHENA readiness inputs" },
    ],
  },
  {
    id: "wf-release-notes",
    name: "Release Notes Generator",
    description:
      "On a release branch, summarize merged PRs into release notes, gate on a human approval, then publish and broadcast.",
    status: "needs-approval",
    env: "production",
    owner: "Priya Nair",
    version: "v3.1",
    risk: "medium",
    triggerHook: "Build Completed",
    lastRun: "1h ago",
    successRate: 0.94,
    avgDuration: "6m 40s",
    runsThisWeek: 12,
    failedRuns: 1,
    hooks: ["hook.build.done"],
    procs: ["proc.release-notes", "proc.approval"],
    pubs: ["pub.release", "pub.slack.post", "pub.confluence"],
    cost: { model: 1.1, cloud: 0.2, verification: 0.0, byoncSavings: 0.9, estRuntime: "7m" },
    history: [
      { version: "v3.1", at: "5d ago", by: "Priya Nair", note: "Awaiting admin approval for external publish" },
      { version: "v3.0", at: "3w ago", by: "Dana Lee", note: "Added Confluence mirror" },
    ],
  },
  {
    id: "wf-garlic-refresh",
    name: "Garlic Specialization Refresh",
    description:
      "When a repo changes significantly, re-train its specialized Garlic agent profile and evaluate the result before saving.",
    status: "paused",
    env: "sandbox",
    owner: "Sven Olsson",
    version: "v1.4",
    risk: "medium",
    triggerHook: "PR Opened",
    lastRun: "2d ago",
    successRate: 0.91,
    avgDuration: "26m 10s",
    runsThisWeek: 4,
    failedRuns: 0,
    hooks: ["hook.github.pr"],
    procs: ["proc.garlic", "proc.verify", "proc.summarize"],
    pubs: ["pub.artifact", "pub.notify"],
    cost: { model: 3.8, cloud: 2.6, verification: 0.6, byoncSavings: 2.9, estRuntime: "26m" },
    history: [
      { version: "v1.4", at: "1w ago", by: "Sven Olsson", note: "Paused — pending eval threshold tuning" },
    ],
  },
  {
    id: "wf-byonc-guardrail",
    name: "BYONC Cost Guardrail",
    description:
      "Detect inference cost spikes, analyze routing, switch to a cost-optimized policy and verify the quality threshold holds.",
    status: "active",
    env: "production",
    owner: "Marcus Webb",
    version: "v5.0",
    risk: "high",
    triggerHook: "Scheduled Run",
    lastRun: "3m ago",
    successRate: 0.987,
    avgDuration: "2m 12s",
    runsThisWeek: 168,
    failedRuns: 1,
    hooks: ["hook.schedule"],
    procs: ["proc.byonc", "proc.cost", "proc.approval"],
    pubs: ["pub.notify", "pub.webhook"],
    controls: { maxCostPerRun: 4, approvalCostThreshold: 3 },
    cost: { model: 0.4, cloud: 0.1, verification: 0.0, byoncSavings: 6.2, estRuntime: "2m" },
    history: [
      { version: "v5.0", at: "6d ago", by: "Marcus Webb", note: "Added approval gate above $3 swing" },
      { version: "v4.3", at: "1mo ago", by: "Marcus Webb", note: "Quality threshold guard" },
    ],
  },
  {
    id: "wf-pr-review",
    name: "PR Review Assistant",
    description:
      "On every opened PR, run an ATHENA readiness check and security review, generate review notes and post them back to GitHub and Slack.",
    status: "active",
    env: "production",
    owner: "Dana Lee",
    version: "v2.2",
    risk: "low",
    triggerHook: "GitHub PR Opened",
    lastRun: "1m ago",
    successRate: 0.971,
    avgDuration: "4m 50s",
    runsThisWeek: 96,
    failedRuns: 3,
    hooks: ["hook.github.pr"],
    procs: ["proc.athena", "proc.security", "proc.test-plan"],
    pubs: ["pub.github.comment", "pub.notify"],
    cost: { model: 1.3, cloud: 0.4, verification: 0.3, byoncSavings: 1.0, estRuntime: "5m" },
    history: [
      { version: "v2.2", at: "3d ago", by: "Dana Lee", note: "Added security regression scan" },
      { version: "v2.1", at: "2w ago", by: "Dana Lee", note: "Trimmed review note verbosity" },
    ],
  },
  {
    id: "wf-security-pipeline",
    name: "Security Regression Pipeline",
    description:
      "Deep security pipeline — risk classify, security review, dependency impact and gated deployment. Still being assembled.",
    status: "broken",
    env: "sandbox",
    owner: "Sven Olsson",
    version: "v0.4",
    risk: "restricted",
    triggerHook: "Deployment Completed",
    lastRun: "—",
    successRate: 0.0,
    avgDuration: "—",
    runsThisWeek: 0,
    failedRuns: 0,
    hooks: ["hook.deploy.done"],
    procs: ["proc.risk", "proc.security", "proc.deps"],
    pubs: ["pub.deploy", "pub.email"],
    incomplete: [2, 5],
    cost: { model: 2.1, cloud: 0.8, verification: 0.7, byoncSavings: 1.4, estRuntime: "11m" },
    history: [
      { version: "v0.4", at: "2d ago", by: "Sven Olsson", note: "Broken — deploy target unset, missing approval gate" },
    ],
  },
];

function buildWorkflow(s: Spec): HiveWorkflow {
  const { nodes, edges } = buildGraph(s.hooks, s.procs, s.pubs);
  if (s.incomplete) {
    s.incomplete.forEach((idx) => {
      if (nodes[idx]) nodes[idx].configComplete = 0.4;
    });
  }
  return {
    id: s.id,
    name: s.name,
    description: s.description,
    status: s.status,
    env: s.env,
    owner: s.owner,
    version: s.version,
    risk: s.risk,
    triggerHook: s.triggerHook,
    lastRun: s.lastRun,
    successRate: s.successRate,
    avgDuration: s.avgDuration,
    runsThisWeek: s.runsThisWeek,
    failedRuns: s.failedRuns,
    integrations: integrationsOf(s.hooks, s.procs, s.pubs),
    nodes,
    edges,
    history: s.history,
    controls: { ...baseControls, ...(s.controls ?? {}) },
    cost: s.cost,
  };
}

export const MOCK_WORKFLOWS: HiveWorkflow[] = SPECS.map(buildWorkflow);

/* ── Templates ────────────────────────────────────────────────────────────── */

export const WORKFLOW_TEMPLATES: WfTemplate[] = [
  {
    id: "tpl-jira-ares",
    name: "Jira Ticket to ARES PR",
    description: "Refine → workspace → branch → ARES → verify → PR, Jira & Slack.",
    category: "Delivery",
    risk: "medium",
    hookKeys: ["hook.jira.created"],
    processKeys: [
      "proc.refine",
      "proc.create-ws",
      "proc.create-branch",
      "proc.assign-ares",
      "proc.verify",
    ],
    publishKeys: ["pub.github.pr", "pub.jira.update", "pub.slack.post"],
  },
  {
    id: "tpl-ci-triage",
    name: "CI Failure Auto-Triage",
    description: "Analyze logs, spin a debug workspace, ask ARES, verify, report.",
    category: "Reliability",
    risk: "medium",
    hookKeys: ["hook.ci.failed"],
    processKeys: ["proc.analyze-logs", "proc.create-ws", "proc.run-ares", "proc.verify"],
    publishKeys: ["pub.github.comment", "pub.slack.post"],
  },
  {
    id: "tpl-api-drift",
    name: "API Drift Watcher",
    description: "ATHENA readiness, SDK drift detection and agent asset generation.",
    category: "API",
    risk: "low",
    hookKeys: ["hook.webhook"],
    processKeys: ["proc.athena", "proc.deps"],
    publishKeys: ["pub.jira.update", "pub.slack.post", "pub.artifact"],
  },
  {
    id: "tpl-garlic",
    name: "Garlic Specialization Refresh",
    description: "Analyze history, refresh specialization, evaluate, save & notify.",
    category: "Agents",
    risk: "medium",
    hookKeys: ["hook.github.pr"],
    processKeys: ["proc.garlic", "proc.verify"],
    publishKeys: ["pub.artifact", "pub.notify"],
  },
  {
    id: "tpl-byonc",
    name: "BYONC Cost Guardrail",
    description: "Analyze routing, switch policy, verify quality, notify admin.",
    category: "Cost",
    risk: "high",
    hookKeys: ["hook.schedule"],
    processKeys: ["proc.byonc", "proc.cost", "proc.approval"],
    publishKeys: ["pub.notify", "pub.webhook"],
  },
  {
    id: "tpl-release-notes",
    name: "Release Notes Generator",
    description: "Summarize merged PRs, human-approve, publish & broadcast.",
    category: "Delivery",
    risk: "medium",
    hookKeys: ["hook.build.done"],
    processKeys: ["proc.release-notes", "proc.approval"],
    publishKeys: ["pub.release", "pub.slack.post"],
  },
];
